// Helpers for the input-fields "wrapper" mechanism, where an input field's
// value lives at a nested location relative to the entity, declared via
// IEntityMaskData.wrapperObjectName. A wrapper may be a dot-path of arbitrary
// depth, e.g. 'address' (one level) or 'supplierProduct.product' (two levels).
//
// These are intentionally pure and dependency-free (no '~/' imports) so they
// can be exercised directly by scripts/wrapperObjectPath.test.ts.

import type { IHasIndexSignature } from '~/types/models'

// Split a wrapperObjectName into its path segments. 'supplierProduct.product'
// -> ['supplierProduct', 'product']. Single-level names yield a one-item array.
export function splitWrapperPath(wrapperObjectName: string): string[] {
  return wrapperObjectName.split('.')
}

// Null-safe nested read. Walks `root` following `segments`, returning the value
// at the path or undefined if any intermediate is missing/non-object.
export function getValueAtWrapperPath(
  root: IHasIndexSignature | undefined | null,
  segments: string[]
): unknown {
  let current: any = root
  for (const segment of segments) {
    if (current === undefined || current === null) {
      return undefined
    }
    current = current[segment]
  }
  return current
}

// Sibling-preserving nested write. Mutates `target` (the accumulating payload),
// walking `segments` and assigning `value` at the final segment. At each
// intermediate segment it replaces target's slot with a fresh shallow copy,
// preferring an intermediate already built on `target` (so multiple fields
// sharing a parent path coexist without clobbering one another) and otherwise
// seeding from the corresponding object in `source` (the original entity) so
// untouched sibling properties carry through to the saved payload. Crucially, a
// fresh copy is made at *every* level so the original `source` entity is never
// mutated at any depth.
export function setValueAtWrapperPath(
  target: IHasIndexSignature,
  segments: string[],
  value: unknown,
  source: IHasIndexSignature | undefined | null
): void {
  let currentTarget = target
  let currentSource: any = source
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i]
    const sourceObject =
      currentSource !== undefined && currentSource !== null
        ? currentSource[segment]
        : undefined
    const existing = currentTarget[segment]
    const base =
      existing !== undefined &&
      existing !== null &&
      typeof existing === 'object'
        ? existing
        : sourceObject !== undefined &&
          sourceObject !== null &&
          typeof sourceObject === 'object'
        ? sourceObject
        : {}
    currentTarget[segment] = { ...base }
    currentTarget = currentTarget[segment]
    currentSource = sourceObject
  }
  currentTarget[segments[segments.length - 1]] = value
}
